import { GlProgram } from 'pixi.js';
import { registerShader } from './shaderTrace';

/**
 * **滤镜四边形自己的坐标。**
 *
 * ## 为什么不能用 `vTextureCoord`
 *
 * Pixi 给滤镜的默认顶点着色器算的是：
 *
 * ```glsl
 * vTextureCoord = aPosition * (uOutputFrame.zw * uInputSize.zw);
 * ```
 *
 * 也就是 `aPosition × (frame 尺寸 / 纹理源尺寸)`。**它不是 [0,1]！**
 * 过滤器要从纹理池里拿一张输入贴图，而纹理池按"2 的幂 / 已注册的屏幕尺寸"
 * 分配：为 1280×800 要到的是一张 1280×800 的贴图（frame == source，恰好 [0,1]），
 * 但为 120×168 要到的是一张 128×256 的 **frame ⊂ source** ——
 * 于是 `vTextureCoord` 只覆盖 `[0, 0.9375] × [0, 0.65625]`。
 *
 * 采样输入贴图时这是**对的**（就得按 frame 在源里的比例走），
 * 但只要着色器拿它算"屏幕上的位置"就全错：坐标被压缩、还偏了。
 *
 * 代价实测过（`card/padFade.ts`）：出血淡出在左边和上边正常、
 * 右边和下边是**硬切**，而且下边切掉了 58 个像素。
 *
 * ## 这个顶点着色器做什么
 *
 * 它**一个字节都不改** Pixi 那套（位置和 `vTextureCoord` 都逐字照抄，
 * 因为片元还要用 `vTextureCoord` 采样输入贴图 —— 少输出一个 varying
 * 就是链接错误），**只多一个**：`vQuadCoord = aPosition`。
 * 滤镜的四边形几何是 `[0,0, 1,0, 1,1, 0,1]`（见 `FilterSystem` 的
 * `quadGeometry`），所以它恰好是 `[0,1]` 铺满整个滤镜区域 ——
 * 和纹理池怎么分配**无关**。
 *
 * ⚠️ 抄来的两个函数是**把四边形放进裁剪空间**的那一步，改动它们
 *    = 画出错位的东西。要改先回去比对 `defaultFilterVert`。
 */
export const QUAD_FILTER_VERT = /* glsl */ `
in vec2 aPosition;
out vec2 vTextureCoord;
out vec2 vQuadCoord;

uniform vec4 uInputSize;
uniform vec4 uOutputFrame;
uniform vec4 uOutputTexture;

// ⚠️ 逐字抄自 pixi.js 的 defaultFilterVert，不要改
vec4 filterVertexPosition( void )
{
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;

    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0*uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;

    return vec4(position, 0.0, 1.0);
}

// ⚠️ 同上。它是**采样输入贴图**用的坐标：frame 在纹理源里占多大就覆盖多大
vec2 filterTextureCoord( void )
{
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
}

void main(void)
{
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
    // [0,1] 铺满滤镜区域。算**位置**要用它，不要用 vTextureCoord
    vQuadCoord = aPosition;
}
`;

/**
 * 拿一个用 `vQuadCoord` 的滤镜程序。
 *
 * 片元着色器里要同时声明 `in vec2 vQuadCoord;`；需要采样输入贴图的地方
 * 仍然用 `vTextureCoord`（那是管线给的、正确的采样坐标）。
 *
 * ⚠️ 每次都把这个 shader 的**别名 `#define` 塞进源码最前面**（并登记它的
 *    sampler 列表，见 `shaderTrace.ts`）：GLSL 报错 / WebGL 的 `unit N` 警告
 *    只会给行号或 unit 号，源码里有一个具名宏才追得回是谁。
 */
export function quadProgram(fragment: string, name: string): GlProgram {
  const alias = registerShader(name, fragment);
  return GlProgram.from({
    vertex: QUAD_FILTER_VERT,
    fragment: '#define ' + alias + ' 1\n' + fragment,
    name,
  });
}
